// src/utils/permissionErrors.ts

export interface PermissionError {
  code: string;
  title: string;
  message: string;
  suggestion?: string;
}

export const PermissionErrors: Record<string, PermissionError> = {
  // Task Management Permissions
  TASK_MANAGEMENT_DISABLED: {
    code: 'TASK_MANAGEMENT_DISABLED',
    title: 'Access Denied',
    message: 'You do not have permission to access Task Management.',
    suggestion: 'Please contact your administrator to enable Task Management access for your role.',
  },
  
  // Assignment Restrictions
  CANNOT_ASSIGN_TO_SELF: {
    code: 'CANNOT_ASSIGN_TO_SELF',
    title: 'Cannot Assign to Self',
    message: 'You cannot assign a task to yourself as Owner.',
    suggestion: 'You can assign tasks to other team members or create tasks directly assigned to you.',
  },
  
  CANNOT_ASSIGN_TO_HIGHER_LEVEL: {
    code: 'CANNOT_ASSIGN_TO_HIGHER_LEVEL',
    title: 'Assignment Restricted',
    message: 'You cannot assign tasks to users at a higher level than you.',
    suggestion: 'Only managers or higher-level users can assign tasks to you. Please request a manager to assign this task.',
  },
  
  CANNOT_ASSIGN_TO_DIFFERENT_BRANCH: {
    code: 'CANNOT_ASSIGN_TO_DIFFERENT_BRANCH',
    title: 'Branch Restriction',
    message: 'You can only assign tasks to users within your branch/office.',
    suggestion: 'Cross-branch assignments require admin approval. Please contact your administrator.',
  },
  
  CANNOT_ASSIGN_TO_INACTIVE_USER: {
    code: 'CANNOT_ASSIGN_TO_INACTIVE_USER',
    title: 'Invalid Assignee',
    message: 'The selected user is inactive or no longer with the organization.',
    suggestion: 'Please select an active employee from the list.',
  },
  
  // Task Modification Restrictions
  CANNOT_MODIFY_COMPLETED_TASK: {
    code: 'CANNOT_MODIFY_COMPLETED_TASK',
    title: 'Task Already Completed',
    message: 'You cannot modify a completed task.',
    suggestion: 'If you need to make changes, please reopen the task first or contact the task creator.',
  },
  
  CANNOT_MODIFY_CANCELLED_TASK: {
    code: 'CANNOT_MODIFY_CANCELLED_TASK',
    title: 'Task Cancelled',
    message: 'This task has been cancelled and cannot be modified.',
    suggestion: 'Contact the task creator if you need to reactivate this task.',
  },
  
  CANNOT_MODIFY_OTHERS_TASK: {
    code: 'CANNOT_MODIFY_OTHERS_TASK',
    title: 'Permission Denied',
    message: 'You do not have permission to modify this task.',
    suggestion: 'Only the task creator, owner, or assignees can modify this task.',
  },
  
  // Status Change Restrictions
  CANNOT_COMPLETE_WITH_OPEN_SUBTASKS: {
    code: 'CANNOT_COMPLETE_WITH_OPEN_SUBTASKS',
    title: 'Cannot Complete Task',
    message: 'This task has open subtasks that need to be completed first.',
    suggestion: 'Please complete all subtasks before marking this task as done.',
  },
  
  CANNOT_REOPEN_COMPLETED_TASK: {
    code: 'CANNOT_REOPEN_COMPLETED_TASK',
    title: 'Cannot Reopen Task',
    message: 'Only the task creator or admin can reopen a completed task.',
    suggestion: 'Please contact the task creator or your manager to reopen this task.',
  },
  
  CANNOT_BLOCK_WITHOUT_REASON: {
    code: 'CANNOT_BLOCK_WITHOUT_REASON',
    title: 'Reason Required',
    message: 'You must provide a reason when blocking a task.',
    suggestion: 'Please explain why this task is blocked to help the team understand the issue.',
  },
  
  // Delete Restrictions
  CANNOT_DELETE_OTHERS_TASK: {
    code: 'CANNOT_DELETE_OTHERS_TASK',
    title: 'Cannot Delete Task',
    message: 'You can only delete tasks that you created.',
    suggestion: 'Only the task creator or administrator can delete this task.',
  },
  
  CANNOT_DELETE_ACTIVE_TASK: {
    code: 'CANNOT_DELETE_ACTIVE_TASK',
    title: 'Cannot Delete Active Task',
    message: 'Please complete or cancel this task before deletion.',
    suggestion: 'Mark the task as completed or cancelled first, then try deleting it.',
  },
  
  // Progress Update Restrictions
  CANNOT_UPDATE_PROGRESS_BEYOND_LIMIT: {
    code: 'CANNOT_UPDATE_PROGRESS_BEYOND_LIMIT',
    title: 'Invalid Progress',
    message: 'Progress cannot exceed 100% or be less than 0%.',
    suggestion: 'Please enter a value between 0 and 100.',
  },
  
  CANNOT_UPDATE_PROGRESS_ON_COMPLETED: {
    code: 'CANNOT_UPDATE_PROGRESS_ON_COMPLETED',
    title: 'Task Already Completed',
    message: 'You cannot update progress on a completed task.',
    suggestion: 'If the task needs more work, please reopen it first.',
  },
  
  // Comment Restrictions
  CANNOT_COMMENT_ON_COMPLETED: {
    code: 'CANNOT_COMMENT_ON_COMPLETED',
    title: 'Cannot Comment',
    message: 'Comments are disabled for completed tasks.',
    suggestion: 'Tasks that are done or cancelled cannot receive new comments.',
  },
  
  CANNOT_EDIT_OTHERS_COMMENT: {
    code: 'CANNOT_EDIT_OTHERS_COMMENT',
    title: 'Cannot Edit Comment',
    message: 'You can only edit your own comments.',
    suggestion: 'Only the comment author can edit or delete their comments.',
  },
  
  // Attachment Restrictions
  FILE_TOO_LARGE: {
    code: 'FILE_TOO_LARGE',
    title: 'File Too Large',
    message: 'The uploaded file exceeds the maximum allowed size of 25MB.',
    suggestion: 'Please compress the file or upload a smaller file.',
  },
  
  UNSUPPORTED_FILE_TYPE: {
    code: 'UNSUPPORTED_FILE_TYPE',
    title: 'Unsupported File Type',
    message: 'This file type is not supported for upload.',
    suggestion: 'Allowed types: Images, PDFs, Documents, Spreadsheets, and Text files.',
  },
  
  // General Errors
  TASK_NOT_FOUND: {
    code: 'TASK_NOT_FOUND',
    title: 'Task Not Found',
    message: 'The requested task could not be found.',
    suggestion: 'The task may have been deleted or you may not have access to it.',
  },
  
  NETWORK_ERROR: {
    code: 'NETWORK_ERROR',
    title: 'Network Error',
    message: 'Unable to connect to the server.',
    suggestion: 'Please check your internet connection and try again.',
  },
  
  UNAUTHORIZED: {
    code: 'UNAUTHORIZED',
    title: 'Session Expired',
    message: 'Your session has expired. Please login again.',
    suggestion: 'Click OK to redirect to the login page.',
  },
};

export const getPermissionError = (errorCode: string): PermissionError => {
  return PermissionErrors[errorCode] || {
    code: 'UNKNOWN_ERROR',
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again.',
    suggestion: 'If the problem persists, please contact support.',
  };
};